import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IAuthResponse } from '@g99-product-web-ui/shared-interface-lib';
import {
  LocalStorageService,
  SubjectManageService,
  ToastService,
} from '@g99-product-web-ui/shared-service';
import {
  LOCAL_STORAGE_KEYS,
  UnionIcon,
} from '@g99-product-web-ui/shared-util-lib';

import { IStoredCredentials } from '../../interface/ICredentials.interface';
import {
  IUserBusinessInfo,
  IUserCredentials,
} from '../../interface/ISignIn.interface';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
  selector: 'g99-business-login',
  templateUrl: './business-selection.component.html',
  styleUrls: ['./business-selection.component.scss'],
  standalone: false,
})
export class BusinessLoginComponent implements OnInit {
  @Input() public userData: IUserBusinessInfo[] = [];

  protected isLoading = false;
  protected businesses: IUserBusinessInfo[] = [];
  protected _credentials: IStoredCredentials | null = null;
  protected svgIcon = UnionIcon;
  protected businessLoader = false;
  protected selectedIndex: number | null = null;

  constructor(
    private readonly _authService: AuthenticationService,
    private readonly _router: Router,
    private readonly _localStorageService: LocalStorageService,
    private readonly _toastService: ToastService,
    private readonly _subjectManageService: SubjectManageService
  ) {}

  /**
   * Redirects the user to the login page if no valid credentials or businesses are found.
   */
  ngOnInit(): void {
    if (!this._hasCredentials()) {
      this.redirectToLogin();
      return;
    }

    this.businesses = this._getBusinessesFromState();
    const isSwitchMode = !!this._credentials?.isSwitchBusiness;

    if (isSwitchMode) {
      this._loadBusinessesFromApi();
      return;
    }

    if (this.businesses.length === 0 && !isSwitchMode) {
      this.redirectToLogin();
    }
  }

  /**
   * Redirects the user to the login page if no valid credentials or businesses are found.
   */
  protected redirectToLogin(): void {
    this._router.navigateByUrl('', { state: {}, replaceUrl: true });
  }

  /**
   * Handles the selection of a business by the user.
   * Performs login using the selected business information and the user's credentials.
   * @param businessInfo - The selected business information, including the tenant ID.
   */
  protected selectBusiness(
    businessInfo: IUserBusinessInfo,
    index: number
  ): void {
    this.selectedIndex = index; // Set the selected index
    const credentials = this._authService.retrieveCredentials();

    if (credentials?.email) {
      this.businessLoader = true;

      const payload: IUserCredentials = {
        ...credentials,
        businessId: businessInfo.tenantId,
      };

      if (payload.isSwitchBusiness) {
        delete payload.isSwitchBusiness;
      }

      this._authService.loginWithCredsAndBid(payload).subscribe({
        next: (res: IAuthResponse | null) => {
          if (res) {
            this.businessLoader = false;
            this._authService.clearCredentials();
            this._localStorageService.removeItem(
              LOCAL_STORAGE_KEYS.AVAILABLE_BUSINESSES
            );

            // Check for return URL and redirect accordingly
            this._handlePostLoginRedirection(res);
          }
        },
        error: (error) => {
          this.businessLoader = false;
          this._toastService.showError('Failed to login');
          if (error.status === 401) {
            this._localStorageService.storeItem('unauthorized_acess', true);
          }
          this.redirectToLogin();
        },
        complete: () => {
          // Handles the case where EMPTY is returned (e.g. 403 — interceptor
          // redirects to change-password). Resets the loader in case the
          // component is still alive (non-navigating 403 edge case).
          this.businessLoader = false;
          this.selectedIndex = null;
        },
      });
    }
  }

  protected onCloseBusinessRestrictionModal(): void {
    this.selectedIndex = null;
    this.businessLoader = false;
  }

  /**
   * Retrieves the list of available businesses from local storage.
   * @returns An array of businesses or an empty array if no businesses are found.
   */
  private _getBusinessesFromState(): IUserBusinessInfo[] {
    const data = this._localStorageService.readItem(
      LOCAL_STORAGE_KEYS.AVAILABLE_BUSINESSES
    );

    // Case 1: No data at all
    if (!data) {
      return [];
    }

    // Case 2: Stored incorrectly as {} (this happens when LS gets cleared or overwritten)
    if (!Array.isArray(data)) {
      return [];
    }

    // Case 3: Valid list returned
    return data;
  }

  /**
   * Checks if valid user credentials are available in the local storage.
   * @returns `true` if valid credentials are found, otherwise `false`.
   */
  private _hasCredentials(): boolean {
    this._credentials = this._authService.retrieveCredentials();
    return !!this._credentials?.email; // allow both normal & switch mode
  }

  /**
   * Loads businesses from the API when in switch mode.
   */
  private _loadBusinessesFromApi(): void {
    if (this._credentials) {
      this.isLoading = true;
      this._authService
        .getAvailableBusinesses(this._credentials.email)
        .subscribe({
          next: (businesses) => {
            this.businesses = businesses;
            this._localStorageService.storeItem(
              LOCAL_STORAGE_KEYS.AVAILABLE_BUSINESSES,
              businesses
            );
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;

            if (
              err.status === 403 &&
              err.error?.message ===
                'Your password has expired. Please update it to continue accessing your account.'
            ) {
              // Handled by auth.interceptor.ts
            } else {
              this.redirectToLogin();
            }
          },
        });
    }
  }

  /**
   * Handles post-login redirection based on whether a return URL exists.
   * If a return URL is present, redirects to that URL.
   * Otherwise, uses the default post-login redirection logic.
   *
   * @param authResponse - The authentication response from successful login
   */
  private _handlePostLoginRedirection(authResponse: IAuthResponse): void {
    const returnUrl = this._localStorageService.readItem<string>(
      LOCAL_STORAGE_KEYS.RETURN_URL
    );

    // Remove the return URL from storage after reading it
    if (returnUrl) {
      this._localStorageService.removeItem(LOCAL_STORAGE_KEYS.RETURN_URL);
    }

    // Redirect to return URL if it exists and is valid, otherwise use default redirection
    if (returnUrl && this._subjectManageService.isValidReturnUrl(returnUrl)) {
      this._router.navigateByUrl(returnUrl);
    } else {
      this._authService.postLoginRedirection(authResponse);
    }
  }
}
